// @ts-check

/**
 * Catalog naming rules shared by the Worker API and the admin dashboard.
 *
 * Categories are human-readable display names (letters, digits, spaces, hyphens, ampersands).
 * Tags are lowercase slugs matching `^[a-z0-9]+(?:-[a-z0-9]+)*$`.
 *
 * Both sanitizers are lossy on purpose: invalid input is silently converted to
 * the closest valid value instead of being rejected, and only fully empty
 * results are treated as invalid by callers.
 *
 * @module shared/catalog
 */

/** Maximum stored length for a category display name. */
export const CATEGORY_MAX_LENGTH = 32;

/** Maximum stored length for a short image title (box-label style). */
export const TITLE_MAX_LENGTH = 40;

/**
 * Preferred minimum length for auto-detected titles.
 * Manual titles may be shorter; analyze expands from the description when under this.
 */
export const TITLE_TARGET_MIN_LENGTH = 15;

/** Maximum stored length for the searchable long-form image description. */
export const DESCRIPTION_MAX_LENGTH = 400;

/** Maximum stored length for a tag slug. */
export const TAG_MAX_LENGTH = 32;

/** Valid tag slug pattern. */
export const TAG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * Catch-all for images whose subject is unknown or not worth deciding yet.
 *
 * Every path that has to name a category without being told one — an admin upload
 * with the field left alone, a player URL import, auto-detect coming up empty —
 * resolves here. Before it existed those paths guessed (`DEFAULT_CATEGORIES[0]`,
 * `'Patterns & Abstract'`) or invented a name no catalog contained (`'Other'`),
 * which left images filed under a subject they did not have or under a category
 * the admin filter could not offer.
 */
export const UNCATEGORIZED_CATEGORY = 'Uncategorized';

/**
 * Categories seeded with every deployment. These cannot be deleted.
 *
 * Appended to, never reordered: the seeded `sort_order` and a few fixtures read
 * by position.
 */
export const DEFAULT_CATEGORIES = [
  'Nature & Landscapes',
  'Animals',
  'Places & Architecture',
  'Art & Illustration',
  'Food & Drink',
  'Transport',
  'Objects & Lifestyle',
  'People & Occasions',
  'Patterns & Abstract',
  UNCATEGORIZED_CATEGORY
];

/**
 * True when a category is one of the seeded defaults and therefore undeletable.
 *
 * @param {unknown} name
 * @returns {boolean}
 */
export function isDefaultCategory(name) {
  const value = String(name ?? '').trim();
  if (!value) return false;
  const normalized = value.toLowerCase();
  return DEFAULT_CATEGORIES.some((preset) => preset.toLowerCase() === normalized);
}

/**
 * Normalizes a category name to the canonical current catalog value.
 *
 * @param {unknown} name
 * @returns {string}
 */
export function normalizeCategoryName(name) {
  const value = String(name ?? '').trim();
  if (!value) return '';
  return value;
}

/**
 * Converts arbitrary input into a valid category display name.
 *
 * Drops unsupported characters, collapses whitespace, trims, and capitalizes
 * each word so names stay consistent with the bundled categories.
 * Example: `" Test !@# Category"` → `"Test Category"`.
 *
 * @param {unknown} raw
 * @returns {string} Sanitized name, or `''` when nothing valid remains.
 */
export function sanitizeCategoryName(raw) {
  if (raw == null) return '';
  const stripped = String(raw)
    .replace(/&amp;/gi, '&')
    .replace(/[^A-Za-z0-9 &\-]+/g, ' ')
    .replace(/\s*&\s*/g, ' & ')
    .replace(/(?:\s*&\s*)+/g, ' & ')
    .replace(/\s*-\s*/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^[ \-&]+|[ \-&]+$/g, '')
    .trim();
  if (!stripped || !/[A-Za-z0-9]/.test(stripped)) return '';

  const titled = stripped.replace(/(^|[ -])([a-z])/g, (_match, prefix, letter) => prefix + letter.toUpperCase());

  return titled.slice(0, CATEGORY_MAX_LENGTH).trim().replace(/[ \-&]+$/g, '');
}

/**
 * Normalizes a short image title for storage.
 *
 * Collapses whitespace, strips control characters, and caps at {@link TITLE_MAX_LENGTH}.
 *
 * @param {unknown} raw
 * @returns {string}
 */
export function sanitizeImageTitle(raw) {
  return String(raw ?? '')
    .replace(/[\u0000-\u001F\u007F]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, TITLE_MAX_LENGTH)
    .trim();
}

/**
 * Normalizes a long-form searchable description (admin-editable, hidden from lobby UI).
 *
 * @param {unknown} raw
 * @returns {string}
 */
export function sanitizeDescription(raw) {
  return String(raw ?? '')
    .replace(/[\u0000-\u001F\u007F]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, DESCRIPTION_MAX_LENGTH)
    .trim();
}

/**
 * Converts arbitrary input into a valid tag slug.
 *
 * Lowercases, replaces unsupported characters with separators, collapses
 * repeated separators, and trims leading/trailing hyphens.
 * Example: `" Test !@# Tag "` → `"test-tag"`.
 *
 * @param {unknown} raw
 * @returns {string} Sanitized slug, or `''` when nothing valid remains.
 */
export function sanitizeTagName(raw) {
  if (raw == null) return '';
  const slug = String(raw)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '');
  if (!slug) return '';
  return slug.slice(0, TAG_MAX_LENGTH).replace(/-+$/g, '');
}

/**
 * Sanitizes a tag list from an array or a comma/space separated string.
 * Empty results are dropped and duplicates are removed in first-seen order.
 *
 * @param {unknown} value
 * @returns {string[]}
 */
export function sanitizeTagList(value) {
  /** @type {unknown[]} */
  let items = [];
  if (Array.isArray(value)) {
    items = value;
  } else if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        items = Array.isArray(parsed) ? parsed : [];
      } catch {
        items = trimmed.split(',');
      }
    } else {
      items = trimmed.split(',');
    }
  } else if (value != null) {
    items = [value];
  }

  const seen = [];
  for (const item of items) {
    const slug = sanitizeTagName(item);
    if (slug && !seen.includes(slug)) seen.push(slug);
  }
  return seen;
}
